import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getDeal } from '../utils/api';

const STATUS_META = {
  active:         { label: 'Active',         cls: 'badge-green' },
  under_contract: { label: 'Under Contract', cls: 'badge-amber' },
  closed:         { label: 'Closed',         cls: 'badge-neutral' },
  dead:           { label: 'Dead',           cls: 'badge-red' },
};

const ROLE_LABELS = { equity: 'Equity', debt: 'Debt', broker: 'Broker', service: 'Service' };

const CAT_META = {
  lp_cogp:        { label: 'LP/Co-GP', cls: 'badge-green', av: 'avatar-lp_cogp' },
  lender:         { label: 'Lender',   cls: 'badge-blue',  av: 'avatar-lender' },
  broker:         { label: 'Broker',   cls: 'badge-purple',av: 'avatar-broker' },
  partner_service:{ label: 'Partner',  cls: 'badge-amber', av: 'avatar-partner_service' },
  seller_operator:{ label: 'Seller/Op', cls: 'badge-teal',  av: 'avatar-seller_operator' },
};

function formatMoney(v) {
  if (!v && v !== 0) return null;
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function PriceCell({ label, value, green }) {
  const formatted = formatMoney(value);
  return (
    <div className="price-cell">
      <div className="price-label">{label}</div>
      <div className={`price-value ${green && formatted ? 'price-green' : ''}`}>
        {formatted || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
      </div>
    </div>
  );
}

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getDeal(id)
      .then(setDeal)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="page-loading">Loading deal…</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!deal) return null;

  const statusMeta = STATUS_META[deal.status] || { label: deal.status, cls: 'badge-neutral' };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <Link to="/deals">Deals</Link> <span>/</span> <span>{deal.name}</span>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 className="page-title" style={{ margin: 0 }}>{deal.name}</h1>
            <span className={`badge ${statusMeta.cls}`}>{statusMeta.label}</span>
          </div>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{deal.address}</div>
        </div>
        <Link to={`/deals/${id}/edit`} className="btn btn-secondary">Edit Deal</Link>
      </div>

      {/* Price grid */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="price-grid">
          <PriceCell label="Whisper Price" value={deal.whisper_price} />
          <PriceCell label="Underwriting Price" value={deal.underwriting_price} />
          <PriceCell label="Purchase Price" value={deal.purchase_price} green />
          <PriceCell label="Equity Amount" value={deal.equity_amount} />
        </div>
        <div className="price-meta-row">
          {deal.close_date && (
            <div className="price-meta-item">
              <span className="price-label">Close Date</span>
              <span>{formatDate(deal.close_date)}</span>
            </div>
          )}
          {deal.driver_name && (
            <div className="price-meta-item">
              <span className="price-label">Relationship Driver</span>
              <span>{deal.driver_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {deal.notes && (
        <div className="detail-section">
          <div className="detail-section-label">Notes</div>
          <div className="notes-text">{deal.notes}</div>
        </div>
      )}

      {/* Linked contacts */}
      <div className="detail-section">
        <div className="detail-section-label">Linked Contacts ({(deal.contacts || []).length})</div>
        {(deal.contacts || []).length === 0 ? (
          <div className="empty-state-inline">No contacts linked.</div>
        ) : (
          <div className="card">
            {(deal.contacts || []).map((c, i) => {
              const catMeta = CAT_META[c.category] || { label: c.category, cls: 'badge-neutral', av: '' };
              return (
                <React.Fragment key={c.id}>
                  <Link to={`/contacts/${c.id}`} className="contact-row">
                    <div className={`contact-avatar ${catMeta.av}`}>
                      {c.first_name?.[0]}{c.last_name?.[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: 'var(--color-text-primary)', fontSize: '0.9rem' }}>
                        {c.first_name} {c.last_name}
                      </div>
                      {c.organization && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{c.organization}</div>
                      )}
                    </div>
                    <span className={`badge ${catMeta.cls}`}>{catMeta.label}</span>
                    <span className="badge badge-neutral">{ROLE_LABELS[c.role] || c.role}</span>
                  </Link>
                  {i < deal.contacts.length - 1 && <div className="row-divider" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
